import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, UserPlus, Languages, Clock } from "lucide-react";
import { ALL_LANGUAGES } from "../data/languages";
import { DISCIPLINE_CODES, DISC_FULL } from "../data/disciplines";

const DISC_LABEL = DISC_FULL;

const blankForm = (user) => ({
  initials: "",
  preferred_language: "Spanish",
  discipline: user?.discipline || "OT",
  notes: "",
});

export default function Patients({ user, onOpenPatient }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankForm(user));

  const load = async () => {
    try {
      const { data } = await api.get("/patients");
      setItems(data);
    } catch {
      toast.error("Could not load patients");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(blankForm(user));
    setOpen(true);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      initials: p.initials,
      preferred_language: p.preferred_language,
      discipline: p.discipline || user.discipline,
      notes: p.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.initials.trim()) {
      toast.error("Initials required");
      return;
    }
    try {
      if (editingId) {
        await api.patch(`/patients/${editingId}`, form);
        toast.success("Patient updated");
      } else {
        await api.post("/patients", form);
        toast.success("Patient added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/patients/${id}`);
      setItems((p) => p.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="space-y-6" data-testid="patients-pane">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-2xl tracking-tight text-zinc-900">Patient cards</h3>
          <p className="text-sm text-zinc-600 mt-1">Save recurring patients so the app remembers their language and history.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-11" onClick={startCreate} data-testid="new-patient-button">
              <UserPlus className="w-4 h-4 mr-2" /> Add patient
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">{editingId ? "Edit patient" : "Add patient"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm uppercase tracking-[0.2em] text-zinc-500">Initials</Label>
                <Input
                  value={form.initials}
                  onChange={(e) => setForm({ ...form, initials: e.target.value })}
                  className="mt-2 h-11"
                  placeholder="J.D."
                  data-testid="patient-initials-input"
                />
              </div>
              <div>
                <Label className="text-sm uppercase tracking-[0.2em] text-zinc-500">Preferred language</Label>
                <Select value={form.preferred_language} onValueChange={(v) => setForm({ ...form, preferred_language: v })}>
                  <SelectTrigger className="mt-2 h-11" data-testid="patient-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-80">
                    {ALL_LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm uppercase tracking-[0.2em] text-zinc-500">Discipline</Label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2">
                  {DISCIPLINE_CODES.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setForm({ ...form, discipline: d })}
                      className={`h-11 rounded-lg border text-sm font-medium ${
                        form.discipline === d ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                      }`}
                      data-testid={`patient-discipline-${d}`}
                    >{d}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-sm uppercase tracking-[0.2em] text-zinc-500">Notes (optional)</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={4}
                  className="mt-2 resize-none"
                  placeholder="Goals, preferences, reminders…"
                  data-testid="patient-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="patient-cancel-button">Cancel</Button>
              <Button onClick={save} data-testid="patient-save-button">{editingId ? "Save changes" : "Add patient"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-zinc-200 rounded-xl p-10 text-center" data-testid="patients-empty">
          <UserPlus className="w-8 h-8 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500">No patients saved yet.</p>
          <p className="text-zinc-400 text-sm mt-1">Add a patient to auto-set their language and recall notes during sessions.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} className="border border-zinc-200 rounded-xl p-5 bg-white flex flex-col" data-testid={`patient-card-${p.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-heading text-2xl tracking-tight text-zinc-900">{p.initials}</p>
                  <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1.5">
                    <Languages className="w-3 h-3" /> {p.preferred_language}
                    <span className="text-zinc-300">·</span>
                    {p.discipline || user.discipline}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => startEdit(p)}
                    className="p-2 text-zinc-400 hover:text-zinc-900 transition-colors"
                    title="Edit"
                    data-testid={`patient-edit-${p.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(p.id)}
                    className="p-2 text-zinc-400 hover:text-red-600 transition-colors"
                    title="Delete"
                    data-testid={`patient-delete-${p.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {p.notes && (
                <p className="text-sm text-zinc-700 leading-relaxed line-clamp-3 flex-1">{p.notes}</p>
              )}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-100">
                <p className="text-xs text-zinc-400 flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  {p.last_seen_at ? `Last seen ${new Date(p.last_seen_at).toLocaleDateString()}` : "Not seen yet"}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8"
                  onClick={() => onOpenPatient?.(p)}
                  data-testid={`patient-open-${p.id}`}
                >
                  Open in session
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
